import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { Button, ButtonSize, ButtonVariant, Text, TextColorVariant, TextVariants } from "@next-platform/client-common-spicy-ui";
import { Spinner } from "@nextui-org/react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { LocalizedLink } from "@/components";
import Trans from "@/components/Trans/Trans";
import { SUPPORT_URL_BY_APPLICATION_ID } from "@/constants";

interface IReportFooterProps {
  submitForm: () => void;
  successMessage: string | null;
  isFormSubmitted?: boolean;
  isValid?: boolean;
  hasFormChanges?: boolean;
  isSubmitting?: boolean;
  submitError: string | null;
  itemType: "character" | "lorebook" | "rulebook";
  isSubmitAttempted?: boolean;
}

const ReportFooter = ({
  submitForm,
  successMessage,
  isFormSubmitted,
  isValid,
  hasFormChanges,
  isSubmitting,
  submitError,
  itemType,
  isSubmitAttempted,
}: IReportFooterProps) => {
  const applicationId = basicAppConfig.applicationId;
  const navigate = useNavigate();
  const supportUrl = applicationId ? SUPPORT_URL_BY_APPLICATION_ID[applicationId as keyof typeof SUPPORT_URL_BY_APPLICATION_ID] : undefined;
  const supportTextTransComponents = useMemo(
    () =>
      supportUrl
        ? {
            supportLink: <LocalizedLink className="text-link" to={supportUrl} target="_blank" rel="noreferrer noopener" aria-label="support-team" />,
          }
        : undefined,
    [supportUrl],
  );
  const onClickCancel = useMemo(
    () => () => {
      navigate("/");
    },
    [navigate],
  );

  return (
    <div className="flex flex-col gap-sm">
      {!isValid && isSubmitAttempted ? (
        <div className="flex flex-row justify-end">
          <Text variant={TextVariants.PARAGRAPH_MEDIUM_REGULAR} color={TextColorVariant.ERROR}>
            {itemType === "character" ? <Trans i18nKey="chatbot:reportPage.form.error.fillAllFields" /> : null}
            {itemType === "lorebook" ? <Trans i18nKey="lorebook:reportPage.form.error.fillAllFields" /> : null}
            {itemType === "rulebook" ? <Trans i18nKey="rulebook:reportPage.form.error.fillAllFields" /> : null}
          </Text>
        </div>
      ) : null}
      {!!submitError && (
        <div className="flex flex-row justify-end">
          <Text variant={TextVariants.PARAGRAPH_MEDIUM_REGULAR} color={TextColorVariant.ERROR}>
            {submitError}
          </Text>
        </div>
      )}
      {!!successMessage && (
        <div className="flex flex-row justify-start">
          <Text variant={TextVariants.PARAGRAPH_MEDIUM_REGULAR} color={TextColorVariant.SUCCESS}>
            {successMessage}
          </Text>
        </div>
      )}
      {supportUrl ? (
        <Text variant={TextVariants.PARAGRAPH_MEDIUM_REGULAR} className="mb-6">
          {itemType === "character" ? <Trans i18nKey="chatbot:reportPage.supportText" components={supportTextTransComponents} /> : null}
          {itemType === "lorebook" ? <Trans i18nKey="lorebook:reportPage.supportText" components={supportTextTransComponents} /> : null}
          {itemType === "rulebook" ? <Trans i18nKey="rulebook:reportPage.supportText" components={supportTextTransComponents} /> : null}
        </Text>
      ) : null}
      <div className="flex flex-row gap-sm">
        <Button aria-labelledby="cancel" onClick={onClickCancel} size={ButtonSize.LARGE}>
          <Trans i18nKey="common:cancel" />
        </Button>
        <Button
          variant={ButtonVariant.SECONDARY}
          type="submit"
          aria-labelledby="save"
          disabled={!hasFormChanges || isSubmitting || isFormSubmitted}
          onClick={submitForm}
          size={ButtonSize.LARGE}
        >
          {(() => {
            if (isSubmitting) {
              return <Spinner color="current" size="sm" />;
            }
            if (itemType === "character") {
              return <Trans i18nKey="chatbot:reportPage.form.submit" />;
            }
            if (itemType === "rulebook") {
              return <Trans i18nKey="rulebook:reportPage.form.submit" />;
            }
            return <Trans i18nKey="lorebook:reportPage.form.submit" />;
          })()}
        </Button>
      </div>
    </div>
  );
};

export default ReportFooter;
